import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { isSensitiveOfflineMutation } from './offlineMutationPolicy';

interface TrivoaDB extends DBSchema {
  documents: {
    key: string;
    value: any;
    indexes: { 'by-date': string };
  };
  offline_mutations: {
    key: number;
    value: {
        url: string;
        method: string;
        body: any;
        timestamp: number;
    };
  };
}

const DB_NAME = 'trivoa-db';
const LEGACY_DB_NAME = ['in', 'voix'].join('') + '-db';
const DB_VERSION = 1;
const PRIVATE_RESPONSE_CACHE_NAMES = ['api-documents-cache'];
let legacyMigration: Promise<void> | undefined;

const openTrivoaDB = () => {
  return openDB<TrivoaDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('documents')) {
        const store = db.createObjectStore('documents', { keyPath: 'id' });
        store.createIndex('by-date', 'createdAt');
      }
      if (!db.objectStoreNames.contains('offline_mutations')) {
        db.createObjectStore('offline_mutations', { keyPath: 'id', autoIncrement: true });
      }
    },
  });
};

const legacyDatabaseExists = async () => {
  try {
    const databases = await (indexedDB as IDBFactory & { databases?: () => Promise<Array<{ name?: string }>> }).databases?.();
    return databases ? databases.some((database) => database.name === LEGACY_DB_NAME) : true;
  } catch {
    return false;
  }
};

const clearLegacyStore = async (db: IDBPDatabase<TrivoaDB>, storeName: 'documents' | 'offline_mutations') => {
  if (!db.objectStoreNames.contains(storeName)) {
    return;
  }

  try {
    await db.clear(storeName);
  } catch {
    // Cleanup is best-effort; a failed clear should not block the new cache.
  }
};

export const migrateLegacyDB = async () => {
  if (!(await legacyDatabaseExists())) {
    return;
  }

  let legacyDB: IDBPDatabase<TrivoaDB> | undefined;

  try {
    legacyDB = await openDB<TrivoaDB>(LEGACY_DB_NAME, DB_VERSION);
    const documents = legacyDB.objectStoreNames.contains('documents')
      ? await legacyDB.getAll('documents')
      : [];
    const offlineMutations = legacyDB.objectStoreNames.contains('offline_mutations')
      ? await legacyDB.getAll('offline_mutations')
      : [];

    if (documents.length === 0 && offlineMutations.length === 0) {
      return;
    }

    const db = await openTrivoaDB();
    const tx = db.transaction(['documents', 'offline_mutations'], 'readwrite');
    await Promise.all([
      ...documents.map((document) => tx.objectStore('documents').put(document)),
      ...offlineMutations.map((mutation) => tx.objectStore('offline_mutations').put(mutation)),
      tx.done,
    ]);
    db.close();

    await clearLegacyStore(legacyDB, 'documents');
    await clearLegacyStore(legacyDB, 'offline_mutations');
  } catch {
    // Legacy cache migration is best-effort; online data remains the source of truth.
  } finally {
    legacyDB?.close();
  }
};

export const initDB = async () => {
  legacyMigration ??= migrateLegacyDB();
  await legacyMigration;
  return openTrivoaDB();
};

export const saveDocumentsToCache = async (documents: any[]) => {
  const db = await initDB();
  const tx = db.transaction('documents', 'readwrite');
  await Promise.all([
    ...documents.map(doc => tx.store.put(doc)),
    tx.done
  ]);
};

export const getCachedDocuments = async () => {
  const db = await initDB();
  return db.getAllFromIndex('documents', 'by-date');
};

export const saveOfflineMutation = async (url: string, method: string, body: any) => {
    if (isSensitiveOfflineMutation(url, body)) {
        throw new Error('Offline queue blocked for sensitive mutation');
    }

    const db = await initDB();
    await db.add('offline_mutations', {
        url,
        method,
        body,
        timestamp: Date.now()
    });
};

export const getOfflineMutations = async () => {
    const db = await initDB();
    return db.getAll('offline_mutations');
};

export const clearOfflineMutations = async () => {
    const db = await initDB();
    await db.clear('offline_mutations');
};

export const clearOfflineData = async () => {
    let db: IDBPDatabase<TrivoaDB> | undefined;
    try {
        db = await initDB();
        const tx = db.transaction(['documents', 'offline_mutations'], 'readwrite');
        await Promise.all([
            tx.objectStore('documents').clear(),
            tx.objectStore('offline_mutations').clear(),
            tx.done,
        ]);
    } finally {
        db?.close();
        if (typeof caches !== 'undefined') {
            await Promise.all(PRIVATE_RESPONSE_CACHE_NAMES.map((cacheName) => caches.delete(cacheName)));
        }
    }
};

export const deleteOfflineMutation = async (id: number) => {
    const db = await initDB();
    await db.delete('offline_mutations', id);
};
